import { useAppSelector } from 'hooks/useAppSelector';
import { getPermissions } from 'reduxStore/reducers/ProfileSlice/selectors/getPermissions';
import { ProfilePermissions } from 'reduxStore/reducers/ProfileSlice/types';

import { ModerationRequestStatusId } from 'enums/moderationRequestStatus';
import { ESubcontractorModerationRequestStatusId } from 'enums/subcontractorModerationRequestStatus';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { moderationRequestActions } from 'reduxStore/reducers/ModerationRequestSlice';
import { subcontractorModerationRequestActions } from 'reduxStore/reducers/SubcontractorModerationSlice';
import { ModerationRequestService } from 'services/ModerationRequestService';
import { SubcontractorModerationRequestService } from 'services/SubcontractorModerationRequestService';
import { Spinner } from 'ui/Spinner';
import { RedesignExecutorModeration } from './ExecutorModeration/RedesignExecutorModeration';
import { FinalStep } from './ExecutorModeration/steps/finalStep';
import { RedesignSubcontractorModeration } from './SubcontractorModeration/RedesignSubcontractorModeration';
import { FinalStepSubcontractor } from './SubcontractorModeration/steps/finalStep';
import { InvitationService } from 'services/InvitationService';
import { profileActions } from 'reduxStore/reducers/ProfileSlice';
import { getIsUserAgentFromInvitation } from 'reduxStore/reducers/ProfileSlice/selectors/getIsUserAgentFromInvitation';

export default function Moderation() {
  const permissions = useAppSelector(getPermissions);
  const dispatch = useAppDispatch();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isModerationRequestLoading, setIsModerationRequestLoading] = useState(true);

  const { data: executorModerationRequest } = useQuery(
    ['getModerationRequest'],
    () => ModerationRequestService.getCurrentModerationRequest(),
    {
      onSuccess: async (data) => {
        if (data.data.status === ModerationRequestStatusId.ElaborationRequested) {
          refetchExecutorWithCheckList();
        } else {
          dispatch(moderationRequestActions.setModerationRequest(data.data));
          setIsModerationRequestLoading(false);
          if ([ModerationRequestStatusId.Draft].includes(data.data.status)) {
            dispatch(moderationRequestActions.setIsFormDisabled(false));
          } else {
            dispatch(moderationRequestActions.setIsFormDisabled(true));
          }
        }
      },
      onError: async () => {
        dispatch(moderationRequestActions.setIsFormDisabled(false));
        setIsModerationRequestLoading(false);
      },
      enabled: !permissions.includes(ProfilePermissions.VerificationQuestionary_Create),
    }
  );

  const { refetch: refetchExecutorWithCheckList } = useQuery(
    ['getExecutorModerationRequestWithCheckLists'],
    () => ModerationRequestService.getCurrentModerationRequestWithCheckLists(),
    {
      onSuccess: async (data) => {
        dispatch(moderationRequestActions.setModerationRequest(data.data));
        setIsModerationRequestLoading(false);
      },
      onError: () => setIsModerationRequestLoading(false),
      enabled: false,
    }
  );

  const {
    data: subcontractorModerationRequest,
    isFetching: isFetchingSubcontractorModerationRequest,
  } = useQuery(
    ['getSubcontractorModerationRequest'],
    () => SubcontractorModerationRequestService.getCurrentModerationRequest(),
    {
      onSuccess: async (data) => {
        if (data.data.statusId === ESubcontractorModerationRequestStatusId.ElaborationRequest) {
          refetchSubcontractorWithCheckList();
        } else {
          dispatch(subcontractorModerationRequestActions.setModerationRequest(data.data));
          if (
            [
              ESubcontractorModerationRequestStatusId.Refused,
              ESubcontractorModerationRequestStatusId.Registered,
              ESubcontractorModerationRequestStatusId.SendToVerification,
              ESubcontractorModerationRequestStatusId.ElaborationProvided,
              ESubcontractorModerationRequestStatusId.Signing,
            ].includes(data.data.statusId)
          ) {
            dispatch(moderationRequestActions.setIsFormDisabled(true));
          }
          if ([ESubcontractorModerationRequestStatusId.Draft].includes(data.data.statusId)) {
            dispatch(moderationRequestActions.setIsFormDisabled(false));
          }
          setIsDisabled(false);
        }
      },
      onError: async () => {
        setIsDisabled(false);
      },
      enabled: permissions.includes(ProfilePermissions.VerificationQuestionary_Create),
    }
  );

  const { refetch: refetchSubcontractorWithCheckList } = useQuery(
    ['getSubcontractorModerationRequestWithCheckLists'],
    () => SubcontractorModerationRequestService.getCurrentModerationRequestWithCheckList(),
    {
      onSuccess: async (data) => {
        dispatch(subcontractorModerationRequestActions.setModerationRequest(data.data));
        setIsDisabled(false);
      },
      enabled: false,
    }
  );

  const isUserAgentFromInvitation = useAppSelector(getIsUserAgentFromInvitation);

  useQuery(['getUserAgentFromInvitation'], () => InvitationService.getUserAgentFromInvitation(), {
    onSuccess: async (data) => {
      dispatch(profileActions.setIsUserAgentFromInvitation(data.data));
    },
    enabled: !Boolean(isUserAgentFromInvitation),
  });

  return (
    <>
      {permissions.includes(ProfilePermissions.VerificationQuestionary_Create) ? (
        <>
          {!isFetchingSubcontractorModerationRequest && !isDisabled ? (
            subcontractorModerationRequest?.data.statusId ? (
              <FinalStepSubcontractor />
            ) : (
              <RedesignSubcontractorModeration />
            )
          ) : (
            <Spinner />
          )}
        </>
      ) : (
        <>
          {isModerationRequestLoading ? (
            <Spinner />
          ) : executorModerationRequest?.data.status ? (
            <FinalStep />
          ) : (
            <RedesignExecutorModeration />
          )}
        </>
      )}
    </>
  );
}
